import { Auth } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useCallback, useEffect, useState } from "react";
import { db } from "../../services/firebase";
import Tracker from "@openreplay/tracker";

export interface UserData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  streetAddress: string;
  city: string;
  region: string;
  postalCode: string;
  plan: "base" | "pro" | "premium";
  planActive: boolean;
}

declare global {
  interface Window {
    tracker: Tracker | null;
  }
}

export const useUser = (auth: Auth | null) => {
  const [userData, setUserData] = useState<UserData | null>(null);

  const fetchUserData = useCallback(
    async (callback: (userData: UserData | null) => void) => {
      if (auth && auth.currentUser) {
        const q = query(
          collection(db, "users"),
          where("uid", "==", auth.currentUser.uid),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const rawData: any = { id: userDoc.id, ...userDoc.data() };
          const tracker = window.tracker;

          if (tracker) {
            tracker.setUserID(rawData.email ?? auth.currentUser.uid);
          }

          callback({
            firstName: rawData.firstName ?? "",
            lastName: rawData.lastName ?? "",
            email: rawData.email ?? "",
            country: rawData.country ?? "",
            streetAddress: rawData.streetAddress ?? "",
            city: rawData.city ?? "",
            region: rawData.region ?? "",
            postalCode: rawData.postalCode ?? "",
            plan:
              rawData.subscription?.status === "active"
                ? "pro"
                : rawData.subscription?.status === "trialing"
                  ? "pro"
                  : "base",
            planActive: rawData.planActive ?? false,
          });
          return;
        }
        callback(null);
      }
    },
    [auth],
  );

  useEffect(() => {
    if (auth) {
      auth.onAuthStateChanged((user) => {
        fetchUserData((data) => {
          setUserData(data);
        });
      });
    }
  }, [fetchUserData, auth, auth?.currentUser]);

  return { userData, fetchUserData };
};
